// MaintenanceVendorAccessPage.jsx
// Public page for maintenance vendors to request access via shared link

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";
import MaintenanceVendorRequestForm from "../components/maintenance-vendor/MaintenanceVendorRequestForm";
import useMaintenanceVendorSubmissions from "../hooks/useMaintenanceVendorSubmissions";
import useMaintenanceVendorLinks from "../hooks/useMaintenanceVendorLinks";
import { getDatabase, ref, get } from "firebase/database";
import { getLocksByProperty } from "../services/PropertyLockService";
import { createAccessCode } from "../services/SmartLocksService";

const EMAIL_SERVER = "https://ai-realtor.onrender.com";

// ── Render cold-start helpers ─────────────────────────────────────────────────
// Render free tier spins down after inactivity; first request after sleep often
// times out. We (1) pre-warm with a lightweight ping, then (2) retry every real
// request up to MAX_RETRIES times with exponential back-off.

const PING_TIMEOUT_MS = 60_000; // up to 60 s for the server to wake
const REQUEST_TIMEOUT_MS = 30_000; // individual request timeout after warm-up
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 2_000;

/** Fire-and-forget ping that waits until the server responds (or times out). */
const warmUpServer = async () => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
  try {
    await fetch(`${EMAIL_SERVER}/health`, { signal: controller.signal });
    console.log("[SERVER] Warm-up ping succeeded");
  } catch (err) {
    // Abort or network error — server may still be starting; proceed anyway
    console.warn("[SERVER] Warm-up ping timed out or failed, proceeding:", err?.message);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Wrapper around fetch with per-attempt timeout + exponential-backoff retries.
 * Throws only after all retries are exhausted.
 */
const fetchWithRetry = async (url, options, retries = MAX_RETRIES) => {
  let lastError;
  for (let attempt = 1; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);
      return res; // success — return regardless of HTTP status (caller checks res.ok)
    } catch (err) {
      clearTimeout(timer);
      lastError = err;
      const isLastAttempt = attempt === retries;
      if (!isLastAttempt) {
        const delay = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1); // 2 s, 4 s, 8 s …
        console.warn(`[FETCH] Attempt ${attempt} failed (${err?.message}), retrying in ${delay}ms…`);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }
  throw lastError;
};

const MaintenanceVendorAccessPage = () => {
  const { listingId, linkId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [vendorLink, setVendorLink] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const { submitMaintenanceVendorRequest } = useMaintenanceVendorSubmissions();
  const { getVendorLink } = useMaintenanceVendorLinks();

  // Pre-warm the Render server as soon as the page loads so it's ready by the
  // time the vendor finishes filling out the form and hits Submit.
  useEffect(() => { warmUpServer(); }, []);

  // ── Fetch property + vendor link ──────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (!listingId) {
        setError("Invalid property link.");
        setLoading(false);
        return;
      }

      try {
        const database = getDatabase();

        const propertyRef = ref(database, `listings/${listingId}`);
        const propertySnapshot = await get(propertyRef);

        if (!propertySnapshot.exists()) {
          setError("Property not found. Please check the link and try again.");
          setLoading(false);
          return;
        }

        const propertyData = propertySnapshot.val();
        setProperty({
          id: listingId,
          address: propertyData.address || "Property",
          city: propertyData.city || "",
          state: propertyData.state || "",
        });

        if (linkId) {
          try {
            const link = await getVendorLink(listingId, linkId);
            if (link && link.status === "active") {
              setVendorLink(link);
            } else {
              setError("This vendor link is no longer active. Please contact the property owner for a new link.");
            }
          } catch (linkErr) {
            console.error("Error fetching vendor link:", linkErr);
            setError("Invalid or expired vendor link. Please contact the property owner.");
          }
        }
      } catch (err) {
        console.error("Error fetching property:", err);
        setError("Failed to load property information. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [listingId, linkId, getVendorLink]);

  // ── Generate access codes and notify vendor ───────────────────────────────
  const generateAndSendAccessCode = useCallback(async (request) => {
    try {
      const locks = await getLocksByProperty(request.listingId);
      if (locks.length === 0) {
        console.warn("[ACCESS CODE] No locks for property", request.listingId);
        return;
      }

      let startsAt = new Date();
      let endsAt = new Date();

      if (request.dateDetails?.startTime) {
        startsAt = new Date(request.dateDetails.startTime);
      }
      if (request.dateDetails?.endTime) {
        endsAt = new Date(request.dateDetails.endTime);
      } else if (request.dateDetails?.startTime) {
        endsAt = new Date(request.dateDetails.startTime);
        endsAt.setHours(endsAt.getHours() + 2);
      } else {
        endsAt = new Date(startsAt.getTime() + 24 * 60 * 60 * 1000);
      }

      if (endsAt <= startsAt) {
        endsAt = new Date(startsAt.getTime() + 2 * 60 * 60 * 1000);
      }

      const startsAtISO = startsAt.toISOString();
      const endsAtISO = endsAt.toISOString();
      const propertyAddress = [request.address, request.city, request.state].filter(Boolean).join(", ");
      const scheduledTime = request.dateDetails?.displayText ||
        `${startsAt.toLocaleDateString()} ${startsAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${endsAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;

      // We need an owner uid to create access codes — fetch from property
      const database = getDatabase();
      const propertyRef = ref(database, `listings/${request.listingId}`);
      const propertySnap = await get(propertyRef);
      const ownerId = propertySnap.exists()
        ? (propertySnap.val().ownerId || propertySnap.val().ownerUid)
        : null;

      if (!ownerId) {
        console.warn("[ACCESS CODE] No ownerId found, skipping code generation");
        return;
      }

      await Promise.all(
        locks.map(async (lock) => {
          const codeName = `Maintenance-${request.companyName || "Vendor"}-${request.requestId}`;
          try {
            const result = await createAccessCode(
              ownerId,
              lock.lockId,
              codeName,
              null,
              startsAtISO,
              endsAtISO,
              false,
              false
            );
            const accessCode = result.accessCode?.access_code?.code;
            const vendorEmail = request.email || "";
            const vendorPhone = request.phone || "";

            if (accessCode && (vendorEmail || vendorPhone)) {
              const response = await fetchWithRetry(`${EMAIL_SERVER}/schedule-access-code-alerts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: vendorEmail,
                  phone: vendorPhone,
                  access_code: accessCode,
                  start_time: startsAtISO,
                  end_time: endsAtISO,
                  address: propertyAddress,
                  viewingDateTimeStr: scheduledTime,
                }),
              });
              if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                console.error("[ACCESS CODE] Alert send failed:", err);
              } else {
                console.log("[ACCESS CODE] ✅ Alert sent for request", request.requestId);
              }
            }
          } catch (codeError) {
            console.error(`[ACCESS CODE] Failed for lock ${lock.lockId}:`, codeError);
          }
        })
      );
    } catch (err) {
      console.error("[ACCESS CODE] Error generating/sending:", err);
    }
  }, []);

  const sendApprovalEmails = useCallback(async (request, landlordEmail) => {
    const propertyAddress = [request.address, request.city, request.state].filter(Boolean).join(", ");
    const scheduledTime = request.dateDetails?.displayText || "Scheduled time not specified";

    // Wake the Render server before firing any requests
    await warmUpServer();

    // Landlord confirmation
    if (landlordEmail) {
      try {
        const res = await fetchWithRetry(`${EMAIL_SERVER}/send-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: landlordEmail,
            subject: `Maintenance Access Request Submitted - ${request.companyName || "Vendor"}`,
            text: `A maintenance vendor has submitted an access request.\n\nVendor: ${request.companyName || "N/A"}\nEmail: ${request.email || "N/A"}\nPhone: ${request.phone || "N/A"}\nProperty: ${propertyAddress}\nScheduled Time: ${scheduledTime}\nRequest ID: ${request.requestId}`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #4caf50;">Maintenance Access Request Submitted</h2>
                <p>A maintenance vendor has submitted an access request for your property.</p>
                <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
                  <h3 style="margin-top: 0;">Request Details:</h3>
                  <p><strong>Vendor Company:</strong> ${request.companyName || "N/A"}</p>
                  <p><strong>Vendor Email:</strong> ${request.email || "N/A"}</p>
                  <p><strong>Vendor Phone:</strong> ${request.phone || "N/A"}</p>
                  <p><strong>Property Address:</strong> ${propertyAddress}</p>
                  <p><strong>Scheduled Time:</strong> ${scheduledTime}</p>
                  <p><strong>Request ID:</strong> ${request.requestId}</p>
                </div>
                <p>The request has been automatically approved. Access codes will be sent to the vendor before their scheduled visit.</p>
              </div>
            `,
          }),
        });
        if (!res.ok) console.error("[APPROVAL EMAIL] Landlord email failed:", await res.json().catch(() => ({})));
        else console.log("[APPROVAL EMAIL] ✅ Landlord email sent to", landlordEmail);
      } catch (err) {
        console.error("[APPROVAL EMAIL] Landlord email error:", err);
      }
    } else {
      console.warn("[APPROVAL EMAIL] No landlord email, skipping.");
    }

    // Vendor confirmation
    const vendorEmail = request.email || "";
    if (vendorEmail) {
      try {
        const res = await fetchWithRetry(`${EMAIL_SERVER}/send-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: vendorEmail,
            subject: `Your Maintenance Access Request Has Been Approved - ${propertyAddress}`,
            text: `Hello ${request.companyName || "Vendor"},\n\nYour maintenance access request has been approved.\n\nProperty: ${propertyAddress}\nScheduled Time: ${scheduledTime}\nRequest ID: ${request.requestId}\n\nAccess codes will be sent to you separately before your scheduled visit.`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #4caf50;">Request Approved!</h2>
                <p>Hello ${request.companyName || "Vendor"},</p>
                <p>Your maintenance access request has been <strong>approved</strong>.</p>
                <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
                  <h3 style="margin-top: 0;">Request Details:</h3>
                  <p><strong>Property Address:</strong> ${propertyAddress}</p>
                  <p><strong>Scheduled Time:</strong> ${scheduledTime}</p>
                  <p><strong>Request ID:</strong> ${request.requestId}</p>
                </div>
                <p>Access codes for the property will be sent to you separately via email and SMS before your scheduled time.</p>
                <p style="margin-top: 30px; color: #666; font-size: 12px;">This is an automated notification. Please do not reply to this email.</p>
              </div>
            `,
          }),
        });
        if (!res.ok) console.error("[APPROVAL EMAIL] Vendor email failed:", await res.json().catch(() => ({})));
        else console.log("[APPROVAL EMAIL] ✅ Vendor approval email sent to", vendorEmail);
      } catch (err) {
        console.error("[APPROVAL EMAIL] Vendor email error:", err);
      }
    } else {
      console.warn("[APPROVAL EMAIL] No vendor email, skipping.");
    }

    // Access codes
    await generateAndSendAccessCode(request);
  }, [generateAndSendAccessCode]);

  // ── Form submit handler ───────────────────────────────────────────────────
  const handleSubmit = async (vendorData, idDocument) => {
    setSubmitting(true);
    try {
      const { success: saved, savedRequest, landlordEmail } =
        await submitMaintenanceVendorRequest(listingId, vendorData, idDocument);

      if (!saved) throw new Error("Submission failed.");

      // Fire emails + access codes immediately — no manual approval needed
      await sendApprovalEmails(savedRequest, landlordEmail);

      setSuccess(true);
      setTimeout(() => navigate("/"), 3000);
    } catch (err) {
      console.error("Error submitting vendor request:", err);
      alert("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const propertyAddress = property
    ? `${property.address}, ${property.city || ""}, ${property.state || ""}`.trim()
    : "";

  return (
    <>
      <Header />
      <Box sx={{ minHeight: "80vh", py: 4 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : success ? (
          <Box sx={{ maxWidth: 600, mx: "auto", p: 3, textAlign: "center" }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your maintenance vendor access request has been submitted and approved!
              You will receive a confirmation email with access code details before your scheduled visit.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Redirecting to home page...
            </Typography>
          </Box>
        ) : (
          <MaintenanceVendorRequestForm
            listingId={listingId}
            propertyAddress={propertyAddress}
            onSubmit={handleSubmit}
            loadingSubmit={submitting}
            predefinedSlots={vendorLink?.slots || null}
            linkId={linkId}
            vendorLink={vendorLink}
          />
        )}
      </Box>
      <Footer />
    </>
  );
};

export default MaintenanceVendorAccessPage;